// Backend
import React from 'react';

// Frontend
import { Grid, Cell } from 'react-foundation';

// Functionality
import { useNavigate, Link } from 'react-router-dom';

// Components
import { Header, ProgressBar, FlashMessage, ErrorRender } from '../../constants/SharedComponents';
import { useDemoContext } from '../../contexts/DemoContext';

export function DemoConfirmation() {
  const navigate = useNavigate();
  const { demoData, updateDemoData, updateDemoPhase } = useDemoContext();
  
  const [loading, setLoading] = React.useState(false);
  const [error] = React.useState(null);
  const [flashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({
    can_share_with_manager: true // Default to checked
  });

  const basicFormButtons = React.useRef([]);

  // Demo data for the selected categories (should match what was selected in categories page)
  const selectedCategories = demoData.selectedCategories || [
    { id: '4af58c57-cbe4-499e-99ec-fb33c45c1b98', name: 'Change' },
    { id: '7185abba-d609-4071-b6b9-6e199c475e38', name: 'Relationships' },
    { id: '8d0b0089-2cd2-4c4f-97ff-588710cba91d', name: 'Demands' }
  ];

  // Demo temperature question data
  const questionScores = {
    questionText: 'In general, how do you find your job?',
    optionText: 'Extremely Stressful'
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate submission delay
    setTimeout(() => {
      // Update demo context to mark as completed
      updateDemoData({
        ...demoData,
        selectedCategories: selectedCategories,
        canShareWithManager: inputs.can_share_with_manager,
        currentStep: 'completed'
      });

      // Update demo phase to prep complete
      updateDemoPhase('prep-complete');

      setLoading(false);
      // Navigate to team member prep results page
      navigate('/demo/team-member-prep-results');
    }, 1000);
  };

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;

    if (event.target.type === 'checkbox') {
      value = event.target.checked;
    }

    setInputs((values) => ({ ...values, [name]: value }));
  };

  const questionResultsContent = (questionCategoryId, field) => {
    // For demo, we don't have complex category data, just return the category name
    return null;
  };

  return (
    <>
      {/* Custom styles for confirmation page checkbox */}
      <style>
        {`
          .demo-confirmation .custom-checkbox {
            position: relative;
            transform: none;
            margin: 0;
            display: flex;
            align-items: flex-start;
            gap: 12px;
            cursor: pointer;
            user-select: none;
            font-family: var(--font--brand-body);
          }
          
          .demo-confirmation .custom-checkbox input {
            display: none;
          }
          
          .demo-confirmation .custom-checkbox .checkmark {
            width: 24px;
            height: 24px;
            border: 2px solid #5f6368;
            border-radius: 4px;
            background-color: #fff;
            box-sizing: border-box;
            position: relative;
            display: inline-block;
            flex-shrink: 0;
            margin-top: 2px;
          }
          
          .demo-confirmation .custom-checkbox input:checked + .checkmark {
            background-color: var(--color--brand-secondary);
            border-color: var(--color--brand-secondary);
          }
          
          .demo-confirmation .custom-checkbox input:checked + .checkmark::after {
            content: "";
            position: absolute;
            left: 6px;
            top: 1px;
            width: 7px;
            height: 14px;
            border: solid #fff;
            border-width: 0 3px 3px 0;
            transform: rotate(45deg);
          }
          
          .demo-confirmation .custom-checkbox span:not(.checkmark) {
            flex: 1;
          }
        `}
      </style>
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      <ErrorRender object={error} />

      <div className="grid-container demo-confirmation">
        <Grid className="grid-padding-x">
          <Cell small={12}>
            <div className="page-content">
              <div className="page-content__header">
                <Link
                  to="/demo/categories-results"
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Back to Team Member Prep
                </Link>
              </div>

              <h1 className="page-content__title">Review</h1>

              <div className="page-content__styles">
                <p>
                  <strong>
                    Please review the following information and click the complete button if
                    you are happy.
                  </strong>
                </p>
                <p>
                  Once you have completed your prep you will not be able to make changes but
                  you will be able to read and download your notes as required.
                </p>
              </div>

              <h2 className="page-content__subtitle">
                The following information will be submitted.
              </h2>

              <form className="basic-form basic-form--background">
                <h3 className="page-content__subtitle page-content__subtitle--small">
                  Key issues to discuss are:
                </h3>
                <ul className="page-content-list--bold">
                  {selectedCategories?.map((questionCategory) => (
                    <li key={'selected-category-' + questionCategory.id}>
                      {questionResultsContent(questionCategory.id, 'optionalCategoryName')
                        ? questionResultsContent(
                          questionCategory.id,
                          'optionalCategoryName',
                        )
                        : questionCategory.name}
                    </li>
                  ))}
                </ul>

                {questionScores && (
                  <>
                    <h3 className="page-content__subtitle page-content__subtitle--small">
                      {questionScores.questionText}
                    </h3>

                    <div className="page-content__styles">
                      <p>
                        <b>{questionScores.optionText}</b>
                      </p>
                    </div>
                  </>
                )}

                <div className="basic-form__field">
                  <label className="custom-checkbox">
                    <input
                      id="can_share_with_manager"
                      name="can_share_with_manager"
                      type="checkbox"
                      value={true}
                      onChange={handleChange}
                      defaultChecked={inputs.can_share_with_manager}
                    />
                    <span className="checkmark"></span>
                    <span className="page-content__subtitle page-content__subtitle--small mb-4">
                      I would like to share this information with my manager.
                    </span>
                  </label>
                </div>

                <div className="basic-form__actions">
                  <input
                    value="Complete"
                    type="submit"
                    className="basic-form__button basic-form__button--inline"
                    onClick={handleSubmit}
                    ref={(ref) => (basicFormButtons.current[0] = ref)}
                    id="page-form-button--submit"
                  />
                </div>

              </form>
            </div>
          </Cell>
        </Grid>
      </div>
    </>
  );
}
